import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSearchParams } from 'react-router';

import { useCustomTableContext } from '@/components/table/context/CustomTable.context';
import {
  CustomTableStateDataModel,
  CustomTableStateModel,
} from '@/components/table/model/CustomTableState.model.ts';
import { useCustomTableInitialParamsSetter } from '@/components/table/util/useCustomTableInitialParamsSetter.util.ts';
import { LocalStorageKey, localStorageUtil } from '@/utils/localStorage/localStorage.util.ts';

interface Props<
  SORT_BY_COLUMN_NAME_MODEL extends string,
  SEARCH_FORM_MODEL extends object,
  ROW_ITEM_MODEL extends object,
> {
  init: (
    initialParams: CustomTableStateModel<
      SORT_BY_COLUMN_NAME_MODEL,
      SEARCH_FORM_MODEL,
      ROW_ITEM_MODEL
    >,
  ) => Promise<CustomTableStateDataModel<ROW_ITEM_MODEL>>;
  initParams: CustomTableStateModel<SORT_BY_COLUMN_NAME_MODEL, SEARCH_FORM_MODEL, ROW_ITEM_MODEL>;
  tableKey: string;
}

export const CustomTableController = <
  SORT_BY_COLUMN_NAME_MODEL extends string,
  SEARCH_FORM_MODEL extends object,
  ROW_ITEM_MODEL extends object,
>({
  init,
  initParams,
  tableKey,
}: Props<SORT_BY_COLUMN_NAME_MODEL, SEARCH_FORM_MODEL, ROW_ITEM_MODEL>) => {
  const [isTableInitialized, setIsTableInitialized] = useState(false);
  const formMethods = useFormContext();
  const [searchParams] = useSearchParams();
  const { customTableInitialParamsSetter } = useCustomTableInitialParamsSetter();
  const { customTableState, setCustomTableState } = useCustomTableContext();
  type CustomTableStateModelInner = CustomTableStateModel<
    SORT_BY_COLUMN_NAME_MODEL,
    SEARCH_FORM_MODEL,
    ROW_ITEM_MODEL
  >;

  const initFn = async () => {
    const initialParams = structuredClone(initParams);
    initialParams.params = cloneDates(initParams.params);
    const tableParams =
      searchParams.get(tableKey) ??
      localStorageUtil.getItem<string>({ key: LocalStorageKey.CUSTOM_TABLE, subKey: tableKey });
    if (tableParams) {
      const parsedTableParams = parseStoredTableParams(tableParams, tableKey);
      if (parsedTableParams) {
        Object.entries(parsedTableParams).forEach(([key, value]) => {
          const parsedKey = key as keyof CustomTableStateModelInner;
          if (parsedKey === 'params') {
            initialParams[parsedKey] = mergeStoredParams(initialParams[parsedKey], value);
            return;
          }

          if (parsedKey === 'page') {
            initialParams[parsedKey] = Number(value) - 1;
            return;
          }

          if (parsedKey === 'pageSize') {
            initialParams[parsedKey] = Number(value);
            return;
          }
          initialParams[parsedKey] = value as keyof CustomTableStateModelInner[typeof parsedKey];
        });
      }
    }
    const data = await init(initialParams);
    setIsTableInitialized(true);
    setCustomTableState(cur => ({
      ...cur,
      ...initialParams,
      data: {
        ...data,
      },
    }));
    formMethods.reset(initialParams.params);
  };

  useEffect(() => {
    void initFn();
  }, []);

  useEffect(() => {
    if (!isTableInitialized) {
      return;
    }
    const newTableParams: Record<string, string> = {};
    Object.entries(customTableState).forEach(([key]) => {
      const parsedKey = key as keyof CustomTableStateModel<
        SORT_BY_COLUMN_NAME_MODEL,
        SEARCH_FORM_MODEL,
        ROW_ITEM_MODEL
      >;
      if (parsedKey === 'data') {
        return;
      }

      if (parsedKey === 'page') {
        newTableParams[parsedKey] = (customTableState[parsedKey] + 1).toString();
        return;
      }

      if (parsedKey === 'params') {
        newTableParams[parsedKey] = JSON.stringify(serializeParams(customTableState[parsedKey]));
        return;
      }

      newTableParams[parsedKey] = customTableState[parsedKey].toString();
    });
    customTableInitialParamsSetter({
      tableSubKey: tableKey,
      value: JSON.stringify(newTableParams),
    });
  }, [customTableState, tableKey, isTableInitialized]);

  return <></>;
};

const SERIALIZED_DATE_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

const cloneDates = <T extends object>(params: T): T => {
  const copy = structuredClone(params);
  Object.entries(params).forEach(([key, value]) => {
    if (dayjs.isDayjs(value)) {
      // @ts-expect-error: proper key of params
      copy[key] = value.clone();
    }
  });
  return copy;
};

const serializeParams = (params: object): Record<string, unknown> => {
  const serialized: Record<string, unknown> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (dayjs.isDayjs(value)) {
      serialized[key] = value.isValid() ? value.toISOString() : null;
      return;
    }
    serialized[key] = value;
  });
  return serialized;
};

const mergeStoredParams = <T extends object>(defaults: T, storedParams: string): T => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(storedParams);
  } catch (error) {
    console.warn('Failed to parse table search params from storage:', error);
    return defaults;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return defaults;
  }

  const defaultValues: Record<string, unknown> = Object.fromEntries(Object.entries(defaults));
  const merged: Record<string, unknown> = { ...defaultValues };
  Object.entries(parsed).forEach(([key, value]) => {
    if (typeof value !== 'string' || !SERIALIZED_DATE_REGEX.test(value)) {
      merged[key] = value;
      return;
    }
    if (typeof defaultValues[key] !== 'string') {
      merged[key] = dayjs(value);
    }
  });
  return merged as T;
};

function parseStoredTableParams(tableParams: string | null, tableKey: string) {
  if (!tableParams) return null;
  try {
    const parsed = JSON.parse(tableParams) as Record<string, string>;
    return parsed;
  } catch (error) {
    console.warn('Failed to parse table params from storage:', error);
    localStorageUtil.clearItem({ key: LocalStorageKey.CUSTOM_TABLE, subKey: tableKey });
    return null;
  }
}
